import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TableContainer,
  TableCell,
  TableBody,
  TableRow,
  Table,
  TableHead,
} from "@mui/material";
import { AddCircleOutline, Close, Edit } from "@mui/icons-material";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomTextBox } from "components/CustomComponents";
import { themes, stickyHeader } from "services/constants";
import { useForm } from "react-hook-form";
import { editIconStyle, validateFormulaPattern } from "services/constants";
import { ConfirmationDialog } from "components/CustomComponents/ConfirmationDialog";
import { childBoxStyle } from "services/constants";
import { CustomButton } from "components/CustomComponents";
import { useSelector } from "react-redux";
import { childBoxStyleSpaceBetween } from "services/constants";
import { customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { Controller } from "react-hook-form";
import { CustomMultiSelectionWithLabel } from "components/CustomComponents/CustomMultiSelectionWithLabel";

const DataTable = ({ data, columns, renderRow, summaryRow }) => (
  <TableContainer sx={{ mb: 2.5, backgroundColor: themes?.whiteColor }}>
    <Table size="small" className="border">
      <TableHead sx={stickyHeader}>
        <TableRow>
          {columns.map((column, index) => (
            <TableCell key={index}>{column}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((item, index) => renderRow(item, index))}
        {summaryRow && summaryRow()}
      </TableBody>
    </Table>
  </TableContainer>
);

export const SalaryComponentTable = ({
  title,
  description,
  data,
  setData,
  totalSalary,
  errors,
  computationMethodChoices,
  filteredComponentLists,
  totalLabel,
  onAddComponent,
  onRemoveComponent,
  onComponentNameChange,
  onComputationMethodChange,
  onAmountChange,
  columns = ["Component", "Method", "Computation", "Amount", ""],
  earningValue,
  onComponentUpdate,
}) => {
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [selectedComponentValues, setSelectedComponentValues] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const totalAmount = useMemo(
    () => data.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
    [data]
  );

  const { domain } = useSelector((state) => state.tokenReducer);

  const {
    register,
    watch,
    setValue,
    reset,
    trigger,
    control,
    formState: { errors: formErrors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      percentage: "",
      value_based_on: [],
      formula: "",
    },
  });

  useEffect(() => {
    if (selectedComponentValues) {
      reset({
        percentage: selectedComponentValues?.computation_value || "",
        value_based_on: selectedComponentValues?.value_based_on || [],
        formula: selectedComponentValues?.value_based_formula || "",
        formula_amount: selectedComponentValues?.amount || 0,
        percentage_amount: selectedComponentValues?.amount || 0,
      });
    }
  }, [selectedComponentValues, reset]);

  const handleCancelClose = () => {
    setShowConfirmationDialog(false);
    setEditingIndex(null);
    setSelectedComponentValues(null);
    setValue("formula_amount", "");
    setValue("percentage_amount", "");
    setIsSubmitDisabled(true);
  };

  const getComponentValue = useCallback(
    (code) => {
      if (!code) return 0;

      const upperCode = code.toUpperCase();
      if (upperCode === "GROSS") {
        return totalSalary;
      }

      const component = earningValue?.find(
        (comp) => comp.code?.toUpperCase() === upperCode
      );
      return component ? parseFloat(component.amount) || 0 : 0;
    },
    [earningValue, totalSalary]
  );

  const calculateFormula = useCallback(
    (formula) => {
      if (!formula || formula.trim() === "") return 0;

      try {
        let expression = formula.toUpperCase().replace(/\s+/g, "");
        const variableValues = {};
        const variableNames = expression.match(/\b[A-Z]+\b/g) || [];
        const uniqueVariables = [...new Set(variableNames)];

        uniqueVariables.forEach((variable) => {
          variableValues[variable] = getComponentValue(variable);
        });

        let evaluatedExpression = expression;
        Object.entries(variableValues).forEach(([key, value]) => {
          const regex = new RegExp(`\\b${key}\\b`, "g");
          evaluatedExpression = evaluatedExpression.replace(
            regex,
            value.toString()
          );
        });

        const calculate = (expr) => {
          while (expr.includes("(")) {
            expr = expr.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
              return calculate(innerExpr);
            });
          }

          const mulDivRegex = /([+-]?\d*\.?\d+)\s*([*/])\s*([+-]?\d*\.?\d+)/;
          while (mulDivRegex.test(expr)) {
            expr = expr.replace(mulDivRegex, (match, a, op, b) => {
              const numA = parseFloat(a);
              const numB = parseFloat(b);
              return op === "*" ? numA * numB : numA / numB;
            });
          }

          const addSubRegex = /([+-]?\d*\.?\d+)\s*([+-])\s*([+-]?\d*\.?\d+)/;
          while (addSubRegex.test(expr)) {
            expr = expr.replace(addSubRegex, (match, a, op, b) => {
              const numA = parseFloat(a);
              const numB = parseFloat(b);
              return op === "+" ? numA + numB : numA - numB;
            });
          }

          return parseFloat(expr) || 0;
        };

        const result = calculate(evaluatedExpression);
        return Number(result.toFixed(2)) || 0;
      } catch (error) {
        console.error("Error calculating formula:", error);
        return 0;
      }
    },
    [getComponentValue]
  );

  const handlePercentage = (event) => {
    if (
      /^(100(\.0{0,2})?|(\d{1,2})(\.\d{0,2})?)$/.test(event.target.value) ||
      event.target.value === ""
    ) {
      setValue("percentage", event.target.value);
      setIsSubmitDisabled(true);
    } else {
      setValue("percentage", "");
    }
  };

  const handleFormula = (event) => {
    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upperValue = input.value.replace(/[a-z]/g, (ch) => ch.toUpperCase());
    setValue("formula", upperValue, { shouldValidate: true });
    requestAnimationFrame(() => {
      input.setSelectionRange(start, end);
    });

    setIsSubmitDisabled(true);
  };

  const handleCalculateFormula = () => {
    const formula = watch("formula");
    if (!formula || formula.trim() === "") return;

    const amount = calculateFormula(formula);
    setValue("formula_amount", amount);
    setIsSubmitDisabled(false);
  };

  const handleCalculatePercentage = () => {
    const percentage = watch("percentage");
    const valueBasedOn = watch("value_based_on");

    if (!percentage || !valueBasedOn || valueBasedOn.length === 0) return;

    let baseAmount = 0;
    if (Array.isArray(valueBasedOn)) {
      valueBasedOn.forEach((dep) => {
        baseAmount += getComponentValue(dep);
      });
    } else {
      baseAmount = getComponentValue(valueBasedOn);
    }

    const calculatedAmount = (parseFloat(percentage) / 100) * baseAmount;
    setValue("percentage_amount", calculatedAmount);
    setIsSubmitDisabled(false);
  };

  const handleValidateFormula = async () => {
    const formula = watch("formula");
    if (!formula || formula.trim() === "") return;

    try {
      const res = await customGetAPI(
        `${domain}HRZone/Salary/Salary_Component/validate-component-value/?value=${formula}`
      );

      if (res.ok) {
        showToast(
          res?.responseDetails?.valid ? "success" : "error",
          res?.responseDetails?.message
        );

        if (res?.responseDetails?.valid) {
          handleCalculateFormula();
        }
      } else {
        showToast("error", res?.message);
      }
    } catch (error) {
      console.error("Error validating formula:", error);
    }
  };

  const validateFormulaVariables = (formula) => {
    if (!formula || !earningValue) {
      return { isValid: true };
    }

    try {
      const expression = formula.toUpperCase().replace(/\s+/g, "");
      const variableNames = expression.match(/\b[A-Z][A-Z0-9]*\b/g) || [];
      const uniqueVariables = [...new Set(variableNames)];

      const availableCodes = earningValue
        .filter((earning) => earning.code && earning.code.trim() !== "")
        .map((earning) => earning.code.toUpperCase());

      const missingVariables = uniqueVariables.filter(
        (variable) => !availableCodes.includes(variable)
      );

      return {
        isValid: missingVariables.length === 0,
        missingVariables,
        availableCodes,
      };
    } catch (error) {
      return {
        isValid: false,
        missingVariables: [],
        availableCodes: [],
        error: error.message,
      };
    }
  };

  const renderRow = (item, index) => (
    <TableRow key={item?.id || index}>
      <TableCell>
        <Box sx={{ position: "relative" }}>
          {data[index]?.component_name?.label === "Special Allowance" ||
          data[index]?.component_name?.label === "Basic" ? (
            <CustomTextBox
              selectedValue={data[index]?.component_name?.label || ""}
              disabledValue={true}
            />
          ) : (
            <CustomAutoComplete
              dataList={filteredComponentLists[index] || []}
              selectedValue={data[index]?.component_name || ""}
              updateDataFunction={(event, value) =>
                onComponentNameChange?.(index, value)
              }
              placeHolder="Select component"
              errorMessage={errors[index]?.component_name}
              textBoxError={!!errors[index]?.component_name}
              disabled={item?.isSpecialAllowance}
            />
          )}
        </Box>
      </TableCell>

      <TableCell>
        <Box sx={{ position: "relative" }}>
          {item?.isSpecialAllowance || item?.isBasic ? (
            <CustomTextBox
              selectedValue={item?.isBasic ? "Percentage" : "Formula"}
              disabledValue={true}
            />
          ) : (
            <CustomAutoComplete
              dataList={computationMethodChoices}
              selectedValue={data[index]?.computation_method || ""}
              updateDataFunction={(event, value) =>
                onComputationMethodChange?.(index, value?.label)
              }
              placeHolder="Select method"
              errorMessage={errors[index]?.computation_method}
              textBoxError={!!errors[index]?.computation_method}
              disableAction={item?.isSpecialAllowance || item?.isBasic}
            />
          )}
        </Box>
      </TableCell>

      <TableCell>
        {data[index]?.computation_method === "Fixed Amount" ? (
          <Box sx={{ width: 100 }}>
            <CustomTextBox
              selectedValue={data[index]?.amount || ""}
              updateDataFunction={(e) => onAmountChange?.(index, e)}
              placeHolder="Enter amount"
              errorMessage={errors[index]?.amount}
              textBoxError={!!errors[index]?.amount}
              disabledValue={item?.isSpecialAllowance}
            />
          </Box>
        ) : data[index]?.computation_method === "Percentage" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CustomTextBox
              selectedValue={
                data[index]?.value_based_percentage || "40% of Gross"
              }
              disabledValue={true}
              errorMessage={errors[index]?.amount}
              textBoxError={!!errors[index]?.amount}
              sx={{ width: 150 }}
            />
            {!item?.isSpecialAllowance ? (
              <Edit
                sx={{ ...editIconStyle, fontSize: 20, cursor: "pointer" }}
                onClick={() => {
                  setSelectedComponentValues(data[index]);
                  setEditingIndex(index);
                  setShowConfirmationDialog(true);
                }}
              />
            ) : null}
          </Box>
        ) : data[index]?.computation_method === "Formula" ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CustomTextBox
              selectedValue={data[index]?.value_based_formula || ""}
              disabledValue={true}
              errorMessage={errors[index]?.amount}
              textBoxError={!!errors[index]?.amount}
              sx={{ width: 150 }}
            />
            {data[index]?.code !== "SP" && (
              <Edit
                sx={{ ...editIconStyle, fontSize: 20, cursor: "pointer" }}
                onClick={() => {
                  setSelectedComponentValues(data[index]);
                  setEditingIndex(index);
                  setShowConfirmationDialog(true);
                }}
              />
            )}
          </Box>
        ) : (
          <CustomTextBox
            selectedValue={""}
            placeHolder="Select method first"
            disabledValue={true}
            sx={{ width: 100 }}
          />
        )}
      </TableCell>

      <TableCell>{Number(data[index]?.amount || 0).toFixed(2)}</TableCell>

      <TableCell>
        {!item?.isSpecialAllowance && !item?.isBasic && (
          <Close
            color="error"
            sx={{ fontSize: 20, cursor: "pointer" }}
            onClick={() => {
              if (onRemoveComponent) {
                onRemoveComponent(item?.id, index);
              }
            }}
          />
        )}
      </TableCell>
    </TableRow>
  );

  const summaryRow = () => (
    <>
      <TableRow>
        <TableCell sx={{ cursor: "pointer" }}>
          <AddCircleOutline sx={{ fontSize: 20 }} onClick={onAddComponent} />
        </TableCell>
        <TableCell colSpan={4} />
      </TableRow>

      <TableRow>
        <TableCell>
          <strong>{totalLabel}</strong>
        </TableCell>
        <TableCell colSpan={2} />
        <TableCell>
          <strong>{totalAmount.toFixed(2)}</strong>
        </TableCell>
        <TableCell />
      </TableRow>
    </>
  );

  return (
    <Box sx={{ mb: 3, position: "relative" }}>
      <ConfirmationDialog
        open={showConfirmationDialog}
        onClose={handleCancelClose}
        onConfirm={async () => {
          const method = selectedComponentValues?.computation_method;

          if (method === "Formula") {
            const isValid = await trigger("formula");
            if (isValid && onComponentUpdate) {
              const updatedComponent = {
                ...data[editingIndex],
                value_based_formula: watch("formula"),
                amount: parseFloat(watch("formula_amount")) || 0,
              };
              onComponentUpdate(editingIndex, updatedComponent);
              handleCancelClose();
            }
            return;
          }

          if (method === "Percentage") {
            const isValid = await trigger(["percentage", "value_based_on"]);
            if (isValid && onComponentUpdate) {
              const updatedComponent = {
                ...data[editingIndex],
                computation_value: watch("percentage"),
                value_based_on: watch("value_based_on"),
                amount: parseFloat(watch("percentage_amount")) || 0,
              };
              onComponentUpdate(editingIndex, updatedComponent);
              handleCancelClose();
            }
            return;
          }
        }}
        title={"Edit Component Value"}
        confirmText={"Submit"}
        cancelText="Cancel"
        showChildren={true}
        handledisabled={isSubmitDisabled}
      >
        <Box sx={childBoxStyle}>
          <Box>
            <Typography color="text.secondary" display="flex" gap={1}>
              <span style={{ fontWeight: 600, width: "90px" }}>Name :</span>
              <strong>
                {selectedComponentValues?.component_name?.label ?? "-"}
              </strong>
            </Typography>
          </Box>

          <Box>
            <Typography color="text.secondary" display="flex" gap={1}>
              <span style={{ fontWeight: 600, width: "90px" }}>Method :</span>
              <strong>
                {selectedComponentValues?.computation_method ?? "-"}
              </strong>
            </Typography>
          </Box>

          {selectedComponentValues?.computation_method === "Percentage" && (
            <Box>
              <Box sx={{ ...childBoxStyleSpaceBetween, gap: 2, px: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>Formula Value</Typography>
                <Box sx={{ width: 100 }}>
                  <CustomTextBox
                    selectedValue={watch("percentage")}
                    rules={{
                      ...register("percentage", {
                        required: "Percentage is required",
                        validate: (value) => {
                          if (!value || value.trim() === "") {
                            return "Percentage is required";
                          }
                          if (
                            parseFloat(value) < 0 ||
                            parseFloat(value) > 100
                          ) {
                            return "Percentage must be between 0 and 100";
                          }
                          return true;
                        },
                      }),
                    }}
                    updateDataFunction={handlePercentage}
                    placeHolder="Enter percentage"
                    errorMessage={formErrors?.percentage?.message ?? ""}
                    textBoxError={Boolean(formErrors.percentage)}
                  />
                </Box>

                <Typography sx={{ fontWeight: 600 }}>Of</Typography>
                <Box sx={{ width: 200 }}>
                  <Controller
                    name="value_based_on"
                    control={control}
                    rules={{
                      required: "Please select at least one component",
                    }}
                    render={({ field }) => {
                      const earningOptions =
                        earningValue
                          ?.filter((comp) => comp.code && comp.code !== "SP")
                          .map((comp) => ({
                            label: comp.code,
                            value: comp.code,
                          })) || [];

                      return (
                        <CustomMultiSelectionWithLabel
                          value={field.value || []}
                          onChange={(newValue) => {
                            field.onChange(newValue);
                            setIsSubmitDisabled(true);
                          }}
                          options={earningOptions}
                          placeholder="Select based on"
                          error={!!formErrors.value_based_on}
                          helperText={formErrors.value_based_on?.message}
                        />
                      );
                    }}
                  />
                </Box>

                <Box>
                  <CustomButton
                    actionName="Calculate"
                    typeName="button"
                    actionFuntion={async () => {
                      const isValid = await trigger([
                        "percentage",
                        "value_based_on",
                      ]);
                      if (isValid) {
                        handleCalculatePercentage();
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {selectedComponentValues?.computation_method === "Formula" && (
            <Box>
              <Box sx={{ ...childBoxStyleSpaceBetween, px: 0, gap: 4 }}>
                <Typography sx={{ fontWeight: 600 }}>Formula</Typography>
                <CustomTextBox
                  selectedValue={watch("formula")}
                  rules={{
                    ...register("formula", {
                      required: "Formula is required",
                      validate: {
                        formulaPattern: (value) => {
                          if (value && !validateFormulaPattern(value)) {
                            return "Invalid formula pattern";
                          }
                          return true;
                        },
                        noSPAllowed: (value) => {
                          if (!value) return true;

                          const hasSP = /\bSP\b/.test(value);
                          if (hasSP) {
                            return "SP is not a valid choice in formula";
                          }
                          return true;
                        },
                        formulaVariables: (value) => {
                          if (!value || value.trim() === "") return true;

                          const validationResult =
                            validateFormulaVariables(value);
                          if (!validationResult.isValid) {
                            return `Invalid component codes: ${validationResult.missingVariables.join(
                              ", "
                            )}`;
                          }
                          return true;
                        },
                      },
                    }),
                  }}
                  updateDataFunction={handleFormula}
                  placeHolder="Enter formula"
                  errorMessage={formErrors?.formula?.message || ""}
                  textBoxError={!!formErrors.formula}
                />

                <Box>
                  <CustomButton
                    actionFuntion={async () => {
                      const isValid = await trigger("formula");
                      if (isValid) {
                        handleValidateFormula();
                      }
                    }}
                    actionName="Validate"
                    typeName="button"
                  />
                </Box>
              </Box>
            </Box>
          )}

          <Box>
            <Typography color="text.secondary" display="flex" gap={1}>
              <span style={{ fontWeight: 600, width: "90px" }}>Amount :</span>
              <strong>
                ₹
                {selectedComponentValues?.computation_method === "Formula"
                  ? parseFloat(watch("formula_amount") || 0).toFixed(2)
                  : parseFloat(watch("percentage_amount") || 0).toFixed(2)}
              </strong>
            </Typography>
          </Box>
        </Box>
      </ConfirmationDialog>

      <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
      <Typography sx={{ fontSize: "0.875rem", color: "#666", mb: 1.5 }}>
        {description}
      </Typography>

      <DataTable
        data={data}
        columns={columns}
        renderRow={renderRow}
        summaryRow={summaryRow}
      />
    </Box>
  );
};
